import React, { useState, useEffect, useCallback } from "react";
import { FileText, CheckCircle2, XCircle, Eye, Download, Search, RefreshCw, AlertCircle } from "lucide-react";
import api from "../../api";

export default function PaymentProofsAdmin() {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadProofs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/payment-proofs");
      setProofs(res.data || []);
    } catch (err) {
      console.error("Fehler beim Laden der Belege:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProofs();
  }, [loadProofs]);

  const handleVerify = async (id) => {
    if (!window.confirm("Zahlungsbeleg genehmigen und Hosting-Konto sofort entsperren?")) return;
    try {
      const res = await api.post(`/admin/payment-proofs/${id}/verify`);
      alert(res.data.message || "Beleg erfolgreich verifiziert!");
      loadProofs();
    } catch (err) {
      alert("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Begründung für die Ablehnung:", "Betrag stimmt nicht überein oder Bankbeleg unleserlich.");
    if (!reason) return;
    try {
      await api.post(`/admin/payment-proofs/${id}/reject`, { reason });
      alert("Beleg abgelehnt und Kunde per E-Mail benachrichtigt.");
      loadProofs();
    } catch (err) {
      alert("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const filtered = proofs.filter((p) => {
    const matchQuery =
      (p.domain || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.customerName || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.fileName || "").toLowerCase().includes(search.toLowerCase());

    if (!matchQuery) return false;
    if (statusFilter === "all") return true;
    return p.status === statusFilter;
  });

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-[#0f172a] flex items-center gap-2">
            <FileText className="text-[#E63946]" size={26} />
            Eingegangene Zahlungsbelege
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Kunden können Zahlungsnachweise direkt hochladen, um gesperrte Accounts umgehend freizuschalten.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadProofs}
            disabled={loading}
            className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-sm transition"
            title="Neu laden"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Domain, Kunde, Datei..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:border-[#E63946] w-64"
            />
          </div>

          <div className="flex items-center gap-2">
            {["all", "pending", "verified", "rejected"].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition capitalize ${
                  statusFilter === st
                    ? "bg-[#0f172a] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {st === "all" ? "Alle" : st === "pending" ? "Ausstehend" : st === "verified" ? "Verifiziert" : "Abgelehnt"}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <FileText size={44} className="mx-auto mb-2 text-slate-300" />
            <div className="font-semibold text-slate-600">Keine Zahlungsbelege gefunden</div>
            <div className="text-xs text-slate-400 mt-1">Sobald ein Kunde einen Beleg hochlädt, erscheint er hier.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((p) => {
              const isPending = p.status === "pending";
              const isVerified = p.status === "verified";
              const isRejected = p.status === "rejected";

              return (
                <div
                  key={p.id}
                  className="p-5 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition flex flex-col md:flex-row md:items-center justify-between gap-4"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-black text-sm text-[#0f172a]">{p.domain}</span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          isPending
                            ? "bg-amber-100 text-amber-800"
                            : isVerified
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {isPending ? "Prüfung ausstehend" : isVerified ? "Genehmigt" : "Abgelehnt"}
                      </span>
                    </div>

                    <div className="text-xs text-slate-500">
                      Kunde: <strong className="text-slate-800">{p.customerName}</strong> ({p.customerEmail}) · Betrag: <strong>CHF {p.amount?.toFixed(2)}</strong>
                    </div>

                    <div className="text-[11px] text-slate-400 font-mono">
                      Datei: {p.fileName} · {(p.fileSize / 1024).toFixed(1)} KB · Eingereicht: {new Date(p.submittedAt).toLocaleString("de-CH")}
                    </div>

                    {p.notes && <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded border border-slate-100 mt-1">Notiz: "{p.notes}"</div>}
                    {p.rejectionReason && <div className="text-xs text-red-600 font-semibold mt-1">Ablehnungsgrund: {p.rejectionReason}</div>}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {p.fileData && (
                      <a
                        href={p.fileData}
                        download={p.fileName || "beleg"}
                        className="px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 text-xs font-semibold inline-flex items-center gap-1.5"
                      >
                        <Eye size={14} /> Beleg öffnen
                      </a>
                    )}

                    {isPending && (
                      <>
                        <button
                          onClick={() => handleVerify(p.id)}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-sm"
                        >
                          <CheckCircle2 size={14} /> Genehmigen & Entsperren
                        </button>
                        <button
                          onClick={() => handleReject(p.id)}
                          className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 font-semibold text-xs border border-red-200"
                        >
                          Ablehnen
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
